import { TRANSACTION_PAGE_TABS } from 'config';
import { STREAM_TYPE } from 'services/CameraStream/config';

// transactionSubStreamConfigEnabled feature flag: the transaction page plays SUB instead of MAIN streams for the cameras
// the backend sends with a SUB entry. The backend alone decides, per desk, from the flag's rules (desk, location, gate,
// camera type, camera, excludes; face match cameras never). The frontend plays SUB while the flag is active (kill
// switch) for a camera that came with a usable SUB entry (hasSubStream), on tiles that are not shown large.

// Transaction tabs only: classic and split view tabs are numeric, the reopened one has its own id. The video wall,
// stream test and VIN check keep the stream type their camera config comes with.
export const isTransactionStreamTab = (tabId) => typeof tabId === 'number' || tabId === TRANSACTION_PAGE_TABS.REOPENED_TRANSACTION_TAB.ID;

// Raw backend camera entries (cameras.level1/level2 of a desk message) -> the same list without a SUB entry that has no
// player (its connect failed) or sits in another media pipeline than its camera's MAIN: the camera's WebRTC connection is
// created from whichever entry is configured, so a bad SUB could break MAIN too. The camera then just plays MAIN.
// Entries are matched to their MAIN by id (the gate camera id); a SUB without a MAIN in the list is kept.
export function withUsableSubStreams(cameras) {
    if (!cameras?.some((camera) => camera.stream_type === STREAM_TYPE.SUB)) return cameras;
    const mainPipelineById = new Map();
    cameras.forEach((camera) => camera.stream_type === STREAM_TYPE.MAIN && mainPipelineById.set(camera.id, pipelineOf(camera)));
    return cameras.filter((camera) => {
        if (camera.stream_type !== STREAM_TYPE.SUB || !mainPipelineById.has(camera.id)) return true;
        const mainPipeline = mainPipelineById.get(camera.id);
        return !!camera.player_id && (!mainPipeline || pipelineOf(camera) === mainPipeline);
    });
}

function pipelineOf(camera) {
    return camera.player_id?.split('/')[0];
}

// Camera ids of the open transactions' cameras that came with a usable SUB entry: AoT warm-up keeps these on the
// stream type they already play, every other camera is warmed up as before.
export function getSubStreamCameraIds(transactions) {
    const cameraIds = new Set();
    transactions?.forEach(({ camsConfig }) => {
        [...(camsConfig?.level1 || []), ...(camsConfig?.level2 || [])].forEach(
            (camConfig) => camConfig.hasSubStream && cameraIds.add(camConfig.camId)
        );
    });
    return cameraIds;
}

// Stream type a camera tile shows: MAIN when it is shown large (expanded, or fullscreen on a transaction tab, the only
// place a non-expanded tile can be on SUB by flag) or forced to MAIN, otherwise its base stream type.
export function resolveShownStreamType({ baseStreamType, isExpanded, isFullScreen, forcedStreamType, tabId }) {
    const isShownLarge = isExpanded || (isFullScreen && isTransactionStreamTab(tabId));
    return isShownLarge || forcedStreamType ? STREAM_TYPE.MAIN : baseStreamType;
}
